import React, { useState, useEffect } from 'react';
// import { useParams } from 'react-router-dom';
import { PlusIcon, HandThumbUpIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../store/authStore';
import apiClient from '../lib/api-client';

interface BacklogItem {
  id: string;
  project_id: string;
  title: string;
  description?: string;
  submitted_by: string;
  status: 'submitted' | 'under_review' | 'accepted' | 'planned' | 'rejected';
  votes: number;
  admin_notes?: string;
  reviewed_by?: string;
  reviewed_at?: string;
  converted_ticket_id?: string;
  created_at: string;
  updated_at: string;
  submitter_name: string;
}

interface CreateBacklogData {
  title: string;
  description?: string;
}

const BacklogPage: React.FC = () => {
  const [projectId, setProjectId] = useState<string>('');
  const [projects, setProjects] = useState<any[]>([]);
  const { user } = useAuthStore();
  const [items, setItems] = useState<BacklogItem[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createData, setCreateData] = useState<CreateBacklogData>({
    title: '',
    description: '',
  });

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    // Fetch projects on mount
    const fetchProjects = async () => {
      try {
        const response = await apiClient.get('/projects');
        const projs = response.data?.data || response.data || [];
        setProjects(projs);
        if (projs.length > 0 && !projectId) {
          setProjectId(projs[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch projects:', err);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    if (projectId) {
      fetchBacklogItems();
    }
  }, [projectId]);

  const fetchBacklogItems = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/projects/${projectId}/backlog`);
      setItems(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch backlog items');
    } finally {
      setLoading(false);
    }
  };

  const createBacklogItem = async () => {
    if (!createData.title || !projectId) return;
    
    try {
      await apiClient.post(`/projects/${projectId}/backlog`, createData);
      setShowCreateForm(false);
      setCreateData({ title: '', description: '' });
      fetchBacklogItems();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create backlog item');
    }
  };

  const voteForItem = async (itemId: string) => {
    try {
      await apiClient.post(`/backlog/${itemId}/vote`);
      fetchBacklogItems();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to vote');
    }
  };

  const updateItemStatus = async (itemId: string, status: string, notes?: string) => {
    try {
      await apiClient.patch(`/backlog/${itemId}`, {
        status,
        admin_notes: notes,
      });
      fetchBacklogItems();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update item');
    }
  };

  const convertToTicket = async (itemId: string) => {
    try {
      const response = await apiClient.post(`/backlog/${itemId}/convert`);
      alert(`Converted to ticket: ${response.data.ticket_id}`);
      fetchBacklogItems();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to convert item');
    }
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      submitted: 'bg-gray-100 text-gray-800',
      under_review: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      planned: 'bg-blue-100 text-blue-800',
      rejected: 'bg-red-100 text-red-800',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            Project Backlog
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Feature requests and ideas from your community.
          </p>
        </div>
        <div className="mt-4 flex md:ml-4 md:mt-0">
          <button
            type="button"
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
          >
            <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
            Submit Idea
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
          <button onClick={() => setError(null)} className="mt-2 text-red-600 underline">
            Dismiss
          </button>
        </div>
      )}

      <div className="mt-8">
        <div className="overflow-hidden bg-white shadow sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {items.map((item) => (
              <li key={item.id} className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-gray-900">{item.title}</h3>
                      <div className="flex items-center space-x-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusBadge(
                            item.status
                          )}`}
                        >
                          {item.status.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                    {item.description && (
                      <p className="mt-1 text-sm text-gray-500">{item.description}</p>
                    )}
                    <div className="mt-2 flex items-center justify-between">
                      <p className="text-xs text-gray-500">
                        Submitted by {item.submitter_name} on{' '}
                        {new Date(item.created_at).toLocaleDateString()}
                      </p>
                      <div className="flex items-center space-x-4">
                        {/* Vote button */}
                        <button
                          onClick={() => voteForItem(item.id)}
                          className="flex items-center text-sm text-gray-500 hover:text-indigo-600"
                        >
                          <HandThumbUpIcon className="h-4 w-4 mr-1" />
                          {item.votes}
                        </button>

                        {/* Admin actions */}
                        {isAdmin && (
                          <div className="flex space-x-2">
                            {item.status === 'submitted' && (
                              <>
                                <button
                                  onClick={() => updateItemStatus(item.id, 'accepted')}
                                  className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded"
                                >
                                  Accept
                                </button>
                                <button
                                  onClick={() => updateItemStatus(item.id, 'rejected')}
                                  className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            {item.status === 'accepted' && (
                              <button
                                onClick={() => convertToTicket(item.id)}
                                className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded"
                              >
                                Convert to Ticket
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {item.admin_notes && (
                      <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                        <strong>Admin Notes:</strong> {item.admin_notes}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Create Item Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Submit Feature Idea</h3>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Title</label>
                <input
                  type="text"
                  value={createData.title}
                  onChange={(e) => setCreateData({ ...createData, title: e.target.value })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={createData.description}
                  onChange={(e) => setCreateData({ ...createData, description: e.target.value })}
                  rows={4}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={createBacklogItem}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BacklogPage;
